import { ANLoadingScreen } from "@answersnow/pkg-theme";
import {
  Autocomplete,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CaretDown, X } from "@phosphor-icons/react";
import React, { useState } from "react";

export interface FamilyNavigatorCandidate {
  id: string;
  firstName: string | null;
  lastName: string | null;
  name: string;
  isActive: boolean | null;
}

interface AssignFamilyNavigatorProps {
  open: boolean;
  onClose: () => void;
  onSelectFamilyNavigator: (candidate: FamilyNavigatorCandidate) => void;
  familyNavigators: FamilyNavigatorCandidate[];
  familyNavigatorsLoading: boolean;
  currentFamilyNavigatorId?: string | null;
}

const getCandidateInitials = (candidate: FamilyNavigatorCandidate): string => {
  return candidate.name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((namePart) => namePart[0].toUpperCase())
    .join("");
};

const AssignFamilyNavigator = ({
  open,
  onClose,
  onSelectFamilyNavigator,
  familyNavigators,
  familyNavigatorsLoading,
  currentFamilyNavigatorId,
}: AssignFamilyNavigatorProps) => {
  const [selectedFamilyNavigator, setSelectedFamilyNavigator] =
    useState<FamilyNavigatorCandidate | null>(null);
  const eligibleFamilyNavigators = familyNavigators.filter(
    (candidate) => candidate.isActive === true && candidate.id !== currentFamilyNavigatorId,
  );

  const handleClose = () => {
    setSelectedFamilyNavigator(null);
    onClose();
  };

  const handleSelect = () => {
    if (!selectedFamilyNavigator) return;

    onSelectFamilyNavigator(selectedFamilyNavigator);
    setSelectedFamilyNavigator(null);
  };

  const renderContent = () => {
    if (familyNavigatorsLoading) {
      return (
        <Stack sx={{ height: 100 }}>
          <ANLoadingScreen />
        </Stack>
      );
    }

    if (eligibleFamilyNavigators.length === 0) {
      return (
        <Typography variant="body1" sx={{ mt: 1 }}>
          NO FAMILY NAVIGATORS AVAILABLE
        </Typography>
      );
    }

    return (
      <Stack gap={3}>
        <Typography
          component="label"
          htmlFor="assign-family-navigator-input"
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 500, lineHeight: 1 }}
        >
          Search Family Navigators
        </Typography>
        <Autocomplete
          id="assign-family-navigator-input"
          options={eligibleFamilyNavigators}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          popupIcon={<CaretDown size={20} />}
          onChange={(_event, newValue) => setSelectedFamilyNavigator(newValue)}
          value={selectedFamilyNavigator}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.id}>
              <ListItemIcon>
                <Avatar sx={{ width: 24, height: 24 }}>{getCandidateInitials(option)}</Avatar>
              </ListItemIcon>
              <Typography variant="body2" sx={{ ml: 1 }}>
                {option.name}
              </Typography>
            </MenuItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Please select"
              size="small"
              fullWidth
              autoFocus
              inputProps={{
                ...params.inputProps,
                "aria-label": "Search Family Navigators",
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          )}
        />
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="assign-family-navigator-dialog-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 3 }}
    >
      <DialogTitle id="assign-family-navigator-dialog-title">
        Assign Family Navigator
        <IconButton aria-label="Close assignment dialog" onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button color="secondary" size="medium" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          size="medium"
          onClick={handleSelect}
          disabled={!selectedFamilyNavigator}
        >
          Assign Family Navigator
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFamilyNavigator;
